import { storage } from './storage';
import {
  createState,
  DEFAULT_BIRTHDAYS,
  emptyEnglish,
  emptyGarden,
  sanitizeBirthday,
  type GameState,
  type Person,
} from './state';

const KEY = 'save';

export function saveGame(s: GameState): void {
  storage.setJSON(KEY, s);
}

export function clearSave(): void {
  storage.remove(KEY);
}

/**
 * Load a save, merging it over a fresh state so that saves from older versions
 * (missing fields) still load, and garbage values are clamped.
 */
export function loadGame(): GameState | null {
  const raw = storage.getJSON<Partial<GameState>>(KEY);
  if (!raw || raw.version !== 3) return null;
  const base = createState({ look: 0, pet: 'rabbit', birthdays: DEFAULT_BIRTHDAYS });
  const s: GameState = {
    ...base,
    ...raw,
    stats: { ...base.stats, ...raw.stats },
    bag: { ...base.bag, ...raw.bag },
    affection: { ...base.affection, ...raw.affection },
    chores: { ...base.chores, ...raw.chores },
    xiaoyu: { ...base.xiaoyu, ...raw.xiaoyu },
    player: { ...base.player, ...raw.player },
    feed: { ...base.feed, ...raw.feed },
    quests: { ...raw.quests },
    vocab: { ...raw.vocab },
    english: { ...emptyEnglish(), ...raw.english },
    garden: emptyGarden().map((p, i) => ({ ...p, ...(Array.isArray(raw.garden) ? raw.garden[i] : undefined) })),
    momTexts: Array.isArray(raw.momTexts) ? raw.momTexts.slice(-40) : [],
    memory: { ...raw.memory },
    journal: Array.isArray(raw.journal) ? raw.journal.slice(-80) : [],
    money: { ...base.money, ...raw.money },
    grade: Number.isFinite(raw.grade) ? Math.max(3, Math.min(6, raw.grade!)) : base.grade,
    hobbies: { ...raw.hobbies },
    posts: Array.isArray(raw.posts) ? raw.posts.slice(-12) : [],
    birthdays: { ...base.birthdays },
  };
  for (const p of Object.keys(base.birthdays) as Person[]) {
    s.birthdays[p] = sanitizeBirthday(raw.birthdays?.[p], base.birthdays[p]);
  }
  if (!Number.isFinite(s.time) || !Number.isFinite(s.dayIndex)) return null;
  return s;
}

export function hasSave(): boolean {
  return loadGame() !== null;
}
